/* Sign-in page — email + password via the platform auth backend.
 *
 * react-hook-form for the form, zod for validation. On success the auth
 * store is populated and the user is redirected to whatever path they
 * tried to visit (via location.state.from) or `/`. */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { z } from 'zod';
import { Button, Card, BrandGlyph } from '@sp/design-system-react';
import { signIn, useAuthStore, classifyAuthError } from '../lib/auth';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email').max(320),
  password: z.string().min(8, 'Password must be at least 8 characters').max(128),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface RedirectState {
  from?: string;
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setSession = useAuthStore((s) => s.setSession);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>();

  // If already signed in, never show the form
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (raw: LoginFormValues): Promise<void> => {
    setServerError(null);
    const parsed = loginSchema.safeParse(raw);
    if (!parsed.success) {
      setServerError(parsed.error.errors[0]?.message ?? 'Invalid input');
      return;
    }
    setSubmitting(true);
    const res = await signIn(parsed.data.email, parsed.data.password);
    setSubmitting(false);
    if (!res.ok) {
      const cls = classifyAuthError(res.error);
      if (cls.isNetwork) {
        setServerError('Unable to reach the server. Check your connection and try again.');
      } else if (cls.isAuth) {
        setServerError('Email or password is incorrect.');
      } else {
        setServerError(res.error || 'Sign-in failed');
      }
      return;
    }
    setSession(res.user, res.tokens);
    const from = (location.state as RedirectState | null)?.from || '/';
    navigate(from, { replace: true });
  };

  return (
    <div className="flex h-full w-full items-center justify-center p-6" style={{ background: 'var(--bg-base)' }}>
      <Card variant="surface" className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3">
          <BrandGlyph size={40} coral alt="SiliconProof AQ" />
          <div className="text-center">
            <h1 className="text-h3" style={{ color: 'var(--ink-primary)' }}>
              Sign in
            </h1>
            <p className="text-caption" style={{ color: 'var(--ink-tertiary)' }}>
              SiliconProof Air Quality
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="email" className="text-label" style={{ color: 'var(--ink-secondary)' }}>
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              {...register('email', { required: true })}
              className="rounded-md border px-3 py-2 text-sm"
              style={{
                background: 'var(--bg-base)',
                borderColor: 'var(--border-hairline)',
                color: 'var(--ink-primary)',
              }}
            />
            {errors.email && (
              <span className="text-caption" style={{ color: 'var(--signal-alarm)' }}>
                {errors.email.message}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="password" className="text-label" style={{ color: 'var(--ink-secondary)' }}>
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              {...register('password', { required: true })}
              className="rounded-md border px-3 py-2 text-sm"
              style={{
                background: 'var(--bg-base)',
                borderColor: 'var(--border-hairline)',
                color: 'var(--ink-primary)',
              }}
            />
            {errors.password && (
              <span className="text-caption" style={{ color: 'var(--signal-alarm)' }}>
                {errors.password.message}
              </span>
            )}
          </div>

          {serverError && (
            <div
              className="rounded-md px-3 py-2 text-caption"
              style={{
                background: 'var(--signal-alarm-soft)',
                color: 'var(--signal-alarm)',
                border: '1px solid var(--signal-alarm)',
              }}
              role="alert"
            >
              {serverError}
            </div>
          )}

          <Button type="submit" variant="primary" size="lg" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
